import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fadeIn } from '../../utils/animations'; // Import fadeIn

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
//import timeGridPlugin from "@fullcalendar/timegrid";

import { EventInput } from '@fullcalendar/core'; // Import EventInput type
import { collection, getDocs, getFirestore, addDoc } from 'firebase/firestore';
import app from '../../config/firebase';

const db = getFirestore(app); // Initialize Firestore

const CalendarPageContainer = styled.div`
  /* Renamed */
  width: 100%;
  height: 85vh; /* Keep specific height for calendar page */
  padding: 1.5rem; /* Adjusted padding */
  background-color: ${(props) => props.theme.colors.background}; /* Updated */
  color: ${(props) => props.theme.colors.text}; /* Updated */
  display: flex;
  flex-direction: column;
  opacity: 0; /* Initial state for page load animation */
  animation: ${fadeIn} 0.5s ease-out forwards; /* Page load animation */

  @media (max-width: 768px) {
    padding: 1rem; /* Reduce padding on smaller screens */
  }

  /* Basic FullCalendar Overrides - adjust selectors as needed */
  .fc {
    /* General text color for the calendar */
    color: ${(props) => props.theme.colors.text};
  }

  .fc .fc-toolbar-title {
    font-size: ${(props) => props.theme.fontSizes.h2};
    font-family: ${(props) => props.theme.fontFamilies.primary};
    color: ${(props) => props.theme.colors.primary};
  }

  .fc .fc-button-primary {
    background-color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) =>
      props.theme.colors
        .surface}; /* Assuming primary is dark enough for light text */
    font-family: ${(props) => props.theme.fontFamilies.primary};
    text-transform: capitalize; /* Prettier buttons */
  }

  .fc .fc-button-primary:hover {
    background-color: ${(props) => props.theme.colors.accentHover};
    border-color: ${(props) => props.theme.colors.accentHover};
  }

  .fc .fc-button-primary:disabled {
    background-color: ${(props) => props.theme.colors.secondary};
    border-color: ${(props) => props.theme.colors.secondary};
  }

  .fc .fc-daygrid-day-number {
    /* Day numbers */
    color: ${(props) => props.theme.colors.textSecondary};
    font-family: ${(props) => props.theme.fontFamilies.secondary};
  }

  .fc .fc-day-today {
    /* Today's date highlight */
    background-color: ${(props) =>
      props.theme.colors.primary}20; /* Light primary transparent */
  }

  /* Event default colors - these can also be set per-event in the data */
  .fc-event {
    background-color: ${(props) => props.theme.colors.secondary};
    border-color: ${(props) => props.theme.colors.secondary};
    color: ${(props) => props.theme.colors.surface}; /* Text on event */
    cursor: pointer; /* Show hand cursor on hover */
    transition: transform 0.2s ease, box-shadow 0.2s ease; /* Smooth hover effect */
  }

  .fc-event:hover {
    transform: translateY(-1px); /* Slight lift effect */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15); /* Subtle shadow on hover */
  }

  .fc-event-main {
    font-family: ${(props) => props.theme.fontFamilies.secondary};
  }

  .fc th {
    /* Header row (Sun, Mon, etc.) */
    font-family: ${(props) => props.theme.fontFamilies.primary};
    color: ${(props) => props.theme.colors.text};
    border-color: ${(props) =>
      props.theme.colors
        .borders} !important; /* Important to override FC styles */
  }

  .fc td,
  .fc th {
    /* All table cells border */
    border-color: ${(props) => props.theme.colors.borders};
  }
`;

// Modal overlay with backdrop blur
const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

// Modal content with slide-up animation
const ModalContent = styled.div<{ $isOpen: boolean }>`
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  transform: ${(props) =>
    props.$isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${(props) => props.theme.borderRadius}
      ${(props) => props.theme.borderRadius} 0 0;
  }
`;

// Close button
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.borders};
    color: ${(props) => props.theme.colors.text};
  }
`;

// Modal header with title
const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.borders};

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }
`;

const EventTitle = styled.h2`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h1};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.fontSizes.h2};
  }
`;

// Modal body with event details
const ModalBody = styled.div`
  padding: 1.5rem 2rem 2rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
`;

const EventDetail = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  color: ${(props) => props.theme.colors.text};
  line-height: 1.6;
`;

const EventDescription = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  color: ${(props) => props.theme.colors.text};
  line-height: 1.7;
  white-space: pre-wrap;
`;

// Event Form Modal
const EventFormModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const EventFormContent = styled.div<{ $isOpen: boolean }>`
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transform: ${(props) =>
    props.$isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: ${(props) => props.theme.borderRadius}
      ${(props) => props.theme.borderRadius} 0 0;
  }
`;

const FormHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.borders};

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }
`;

const FormTitle = styled.h2`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h2};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.fontSizes.h3};
  }
`;

const FormBody = styled.div`
  padding: 1.5rem 2rem 2rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}40;
  }
`;

const TimeInputGroup = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}40;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${(props) => props.theme.borderRadius};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) =>
    props.$variant === 'secondary'
      ? props.theme.colors.secondary
      : props.theme.colors.primary};
  color: ${(props) => props.theme.colors.surface};

  &:hover {
    background-color: ${(props) =>
      props.$variant === 'secondary'
        ? props.theme.colors.textSecondary
        : props.theme.colors.accentHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  background-color: ${(props) => props.theme.colors.success};
  color: white;
  padding: 1rem 1.5rem;
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  animation: ${fadeIn} 0.3s ease-out;
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-weight: 600;

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    left: 1rem;
  }
`;

// Define a more specific type for your Firestore event data
interface FirestoreEventData {
  id: string;
  title: string;
  date: string; // Assuming date is stored as a string like 'YYYY-MM-DD'
  timefrom?: string;
  timeto?: string;
  description?: string;
  // any other properties from Firestore
}

// Extended event interface for modal
interface ExtendedEventData extends FirestoreEventData {
  start?: string;
  end?: string;
}

// Event form data interface
interface EventFormData {
  title: string;
  date: string;
  timefrom: string;
  timeto: string;
  description: string;
}

const CalendarEventsComponent: React.FC = () => {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ExtendedEventData | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    date: '',
    timefrom: '',
    timeto: '',
    description: '',
  });

  const fetchEvents = async () => {
    const querySnapshot = await getDocs(collection(db, 'events'));
    const eventList: EventInput[] = querySnapshot.docs.map((doc) => {
      const data = doc.data() as FirestoreEventData;
      return {
        id: doc.id,
        title: data.title,
        start: data.date,
        extendedProps: {
          description: data.description,
          timefrom: data.timefrom,
          timeto: data.timeto,
          originalData: data,
        },
      };
    }) as EventInput[];
    setEvents(eventList);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleEventClick = (info: any) => {
    const eventData = info.event.extendedProps
      .originalData as FirestoreEventData;
    setSelectedEvent({
      ...eventData,
      start: info.event.startStr,
      end: info.event.endStr,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setFormData({
      title: '',
      date: '',
      timefrom: '',
      timeto: '',
      description: '',
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleFormOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeForm();
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'events'), formData);
      setShowSuccess(true);
      closeForm();
      await fetchEvents(); // Refetch events

      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <>
      <CalendarPageContainer>
        <FullCalendar
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          plugins={[dayGridPlugin]}
          events={events}
          height={'100%'}
          eventClick={handleEventClick}
        />
      </CalendarPageContainer>

      {/* Event Details Modal */}
      <ModalOverlay $isOpen={isModalOpen} onClick={handleOverlayClick}>
        <ModalContent $isOpen={isModalOpen}>
          <CloseButton onClick={closeModal}>×</CloseButton>

          {selectedEvent && (
            <>
              <ModalHeader>
                <EventTitle>{selectedEvent.title}</EventTitle>
              </ModalHeader>

              <ModalBody>
                <EventDetail>
                  <DetailLabel>Date</DetailLabel>
                  <DetailValue>{formatDate(selectedEvent.date)}</DetailValue>
                </EventDetail>

                {(selectedEvent.timefrom || selectedEvent.timeto) && (
                  <EventDetail>
                    <DetailLabel>Time</DetailLabel>
                    <DetailValue>
                      {selectedEvent.timefrom &&
                        formatTime(selectedEvent.timefrom)}
                      {selectedEvent.timefrom && selectedEvent.timeto && ' - '}
                      {selectedEvent.timeto && formatTime(selectedEvent.timeto)}
                    </DetailValue>
                  </EventDetail>
                )}

                {selectedEvent.description && (
                  <EventDetail>
                    <DetailLabel>Description</DetailLabel>
                    <EventDescription>
                      {selectedEvent.description}
                    </EventDescription>
                  </EventDetail>
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </ModalOverlay>

      {/* Add Event Form Modal */}
      <EventFormModal $isOpen={isFormOpen} onClick={handleFormOverlayClick}>
        <EventFormContent $isOpen={isFormOpen}>
          <FormHeader>
            <FormTitle>Add New Event</FormTitle>
          </FormHeader>

          <FormBody>
            <form onSubmit={handleFormSubmit}>
              <FormField>
                <FormLabel>Event Title</FormLabel>
                <FormInput
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder="Enter event title"
                />
              </FormField>

              <FormField>
                <FormLabel>Date</FormLabel>
                <FormInput
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                />
              </FormField>

              <FormField>
                <FormLabel>Time Range</FormLabel>
                <TimeInputGroup>
                  <FormInput
                    type="time"
                    name="timefrom"
                    value={formData.timefrom}
                    onChange={handleFormChange}
                    placeholder="Start time"
                  />
                  <FormInput
                    type="time"
                    name="timeto"
                    value={formData.timeto}
                    onChange={handleFormChange}
                    placeholder="End time"
                  />
                </TimeInputGroup>
              </FormField>

              <FormField>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter event description"
                />
              </FormField>

              <FormActions>
                <FormButton
                  type="button"
                  $variant="secondary"
                  onClick={closeForm}
                >
                  Cancel
                </FormButton>
                <FormButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding Event...' : 'Add Event'}
                </FormButton>
              </FormActions>
            </form>
          </FormBody>
        </EventFormContent>
      </EventFormModal>

      {/* Success Message */}
      {showSuccess && (
        <SuccessMessage>Event added successfully!</SuccessMessage>
      )}
    </>
  );
};

export default React.memo(CalendarEventsComponent);
